import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Trash2 } from 'lucide-react';
import { useChatbot } from '../../hooks/useChatbot';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';
import ChatQueueBanner from './ChatQueueBanner';

// Quarter-circle corner FAB — pinned bottom-0 right-0, semi-transparent idle, opaque on hover/focus.
export default function ChatWidget() {
  const { t, i18n } = useTranslation();
  const { messages, isStreaming, queuePosition, send, load, clear } = useChatbot();
  const [isOpen, setIsOpen] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && !historyLoaded) {
      load();
      setHistoryLoaded(true);
    }
  }, [isOpen, historyLoaded, load]);

  const aiLabel = i18n.language?.startsWith('en') ? 'AI' : 'IA';

  return ReactDOM.createPortal(
    <>
      <button
        onClick={() => setIsOpen(o => !o)}
        className="fixed bottom-0 right-0 z-[10000] w-16 h-16 rounded-tl-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg opacity-70 hover:opacity-100 focus-visible:opacity-100 flex items-center justify-center pl-2.5 pt-2.5 hover:shadow-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-600"
        aria-label={t('chatbot.launcher', "Besoin d'aide ?")}
      >
        <MessageCircle size={20} className="translate-x-[2px] translate-y-[2px]" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="fixed bottom-24 right-4 z-[10000] w-[26rem] max-w-[calc(100vw-2rem)] h-[36rem] max-h-[calc(100vh-12rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{t('chatbot.title', 'Assistant Ebiosens')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/20 font-medium tracking-wide">{aiLabel}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={clear}
                  className="p-1 rounded hover:bg-white/20"
                  aria-label={t('chatbot.clear', "Effacer l'historique")}
                >
                  <Trash2 size={16} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded hover:bg-white/20"
                  aria-label={t('chatbot.close', 'Fermer')}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <ChatMessageList
              messages={messages}
              emptyHint={t('chatbot.empty', "Posez une question sur l'application ou la méthode EBIOS RM.")}
              onPromptClick={send}
            />
            <ChatQueueBanner position={queuePosition} />
            <ChatInput onSend={send} disabled={isStreaming} />
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
}
